/**
 * OfflineBanner — top-of-app strip that surfaces either:
 *
 *   1. OFFLINE (amber) — only on competition surfaces
 *      (`/gym/{slug}#comp`, `/dashboard/...`, `/tv/...`, `/series/...`).
 *      The "syncs automatically when the connection returns" promise
 *      holds only for queue-backed flows (`src/utils/judgeQueue.js`),
 *      so we don't show it elsewhere (map, settings, etc.) where the
 *      promise would lie. 2 s debounce kills sub-second blip flicker.
 *
 *   2. STALLED (amber) — anywhere, regardless of `navigator.onLine`.
 *      Shown when the queue holds items that have been waiting longer
 *      than STALL_MS. This is the congested-gym-wifi case the offline
 *      banner is blind to: the phone is associated, `navigator.onLine`
 *      is true, but Firestore is unreachable and every write times out
 *      at 10 s and retries. Without this the competitor gets a
 *      "saved" toast, sees no banner, and has no way to learn that
 *      thirty routes are sitting unflushed. The data is safe in IDB —
 *      this is an honesty banner, and it also gives them a chance to
 *      find signal BEFORE the organizer closes the comp and turns the
 *      whole queue into blocked (red) items.
 *
 *   3. BLOCKED (red) — anywhere, regardless of network state. Shown
 *      when the queue has items the Firestore rules permanently
 *      rejected (typically: comp was closed / attempt registration
 *      locked while the event was in the local IDB queue). These
 *      need human action — either the organizer re-opens attempt
 *      registration (next wake-up flush picks them up automatically)
 *      or the user discards them via the X button on the banner.
 *      Surfaces everywhere because it's data-loss-imminent: a
 *      competitor who navigated to the map should still know their
 *      offline saves never made it.
 *
 * The blocked banner takes priority — if several conditions are true we
 * show the red one because it needs action; the amber ones are info. Of
 * the two amber cases, an explicit offline state wins on a competition
 * surface: "you are offline, it will sync" is the more useful sentence
 * than "N saves are waiting".
 *
 * Mounted once at the App root (inside ToastProvider), next to
 * UpdateBanner. Going back online dismisses immediately — "you're
 * online again" is welcome news the user shouldn't wait for.
 */
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router";
import { F } from "../constants";
import { useTranslation } from "../i18n";
import { subscribeJudgeQueue, discardBlockedJudgeEvents } from "../utils/judgeQueue";
import { useConfirm, IconBtn, IconClose } from "./ui";

const SHOW_DELAY_MS = 2000;
/** How long a queued write may sit before we admit it is not going through.
 *  Comfortably above one 10 s write timeout in `judgeQueue`, so a single slow
 *  round-trip doesn't raise a banner. */
const STALL_MS = 15000;
/** Item age is wall-clock, so it grows without a queue event to react to. */
const STALL_POLL_MS = 5000;

function isCompetitionContext(location) {
  if (!location) return false;
  const path = location.pathname || "";
  const hash = location.hash || "";
  if (path.startsWith("/gym/") && hash.startsWith("#comp")) return true;
  if (path.startsWith("/dashboard/")) return true;
  if (path.startsWith("/tv/")) return true;
  if (path.startsWith("/series/")) return true;
  return false;
}

export function OfflineBanner() {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const location = useLocation();
  const inComp = isCompetitionContext(location);

  const [isOnline, setIsOnline] = useState(
    typeof navigator === "undefined" ? true : navigator.onLine !== false
  );
  const [show, setShow] = useState(false);
  const [queueItems, setQueueItems] = useState([]);
  const [discarding, setDiscarding] = useState(false);
  // Ticks only while something is pending, purely to re-evaluate item age.
  const [, setAgeTick] = useState(0);

  useEffect(() => {
    const on = () => setIsOnline(true);
    const off = () => setIsOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);

  // Track the queue globally so both warnings surface no matter which tab the
  // user is on.
  useEffect(() => {
    const unsub = subscribeJudgeQueue((items) => {
      setQueueItems(Array.isArray(items) ? items.filter(Boolean) : []);
    });
    return unsub;
  }, []);

  const blockedCount = queueItems.filter((it) => it.blocked).length;
  const pendingCount = queueItems.length - blockedCount;

  useEffect(() => {
    if (pendingCount === 0) return undefined;
    const id = setInterval(() => setAgeTick((n) => n + 1), STALL_POLL_MS);
    return () => clearInterval(id);
  }, [pendingCount]);

  const oldestPendingAt = queueItems.reduce((oldest, it) => {
    if (it.blocked || !it.enqueuedAt) return oldest;
    return oldest === null ? it.enqueuedAt : Math.min(oldest, it.enqueuedAt);
  }, null);
  const stalled = pendingCount > 0 && oldestPendingAt !== null
    && (Date.now() - oldestPendingAt) > STALL_MS;

  useEffect(() => {
    if (isOnline) {
      setShow(false);
      return;
    }
    const timer = setTimeout(() => setShow(true), SHOW_DELAY_MS);
    return () => clearTimeout(timer);
  }, [isOnline]);

  const handleDiscardBlocked = async () => {
    if (discarding) return;
    // OfflineBanner is mounted inside the ToastProvider (which also
    // provides the ConfirmCtx), so the styled useConfirm() works here.
    // Destructive — discarding the items deletes real recorded climbs
    // that simply couldn't reach the server.
    const ok = await confirm({
      title: t("offline.blockedDiscardTitle"),
      body: t("offline.blockedDiscardConfirm", { n: blockedCount }),
      danger: true,
      confirmLabel: t("offline.blockedDiscardConfirmLabel"),
    });
    if (!ok) return;
    setDiscarding(true);
    try { await discardBlockedJudgeEvents(); }
    finally { setDiscarding(false); }
  };

  // Priority 1: BLOCKED items — always show, anywhere, in red.
  if (blockedCount > 0) {
    return (
      <div role="alert" aria-live="assertive" style={{
        background: "#dc2626", color: "#fff",
        padding: "8px 16px",
        display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
        fontSize: 13, fontWeight: 700, fontFamily: F,
      }}>
        <span aria-hidden="true">🚫</span>
        <span style={{ flex: "0 1 auto", textAlign: "center" }}>
          {t("offline.blockedBanner", { n: blockedCount })}
        </span>
        <IconBtn
          icon={IconClose} size={14} padding={0}
          onClick={handleDiscardBlocked}
          disabled={discarding}
          title={t("offline.blockedDiscardAria")}
          style={{
            marginLeft: 4,
            background: "rgba(255,255,255,0.18)",
            border: "1px solid rgba(255,255,255,0.5)",
            color: "#fff",
            width: 24, height: 24, borderRadius: 999,
            cursor: discarding ? "not-allowed" : "pointer",
          }}
        />
      </div>
    );
  }

  // Priority 2: OFFLINE in competition context — amber info banner.
  if (show && inComp) {
    return (
      <div role="status" aria-live="polite" style={{
        background: "#f59e0b", color: "#fff",
        padding: "8px 16px",
        display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
        fontSize: 13, fontWeight: 700, fontFamily: F,
      }}>
        <span aria-hidden="true">📴</span>
        <span>{t("offline.banner")}</span>
      </div>
    );
  }

  // Priority 3: writes queued and not draining — the "online but Firestore is
  // unreachable" case. Shown anywhere, because the queue items are the subject
  // and they follow the user off the competition tab.
  if (stalled) {
    return (
      <div role="status" aria-live="polite" style={{
        background: "#f59e0b", color: "#fff",
        padding: "8px 16px",
        display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
        fontSize: 13, fontWeight: 700, fontFamily: F,
      }}>
        <span aria-hidden="true">🐌</span>
        <span style={{ textAlign: "center" }}>{t("offline.pendingBanner", { n: pendingCount })}</span>
      </div>
    );
  }

  return null;
}
